import { useMemo, useState } from 'react';
import { CtaButton, ModalControlVariables } from './styled';
import type { Period } from '../../../types/portfolioDividend';
import { extractDividendQualityByPeriod } from '../../../utils/extractPorfolioDividendData';
import { usePortfolioHoldingsDividendForecastCreator } from '../../../hooks';
import { Button, Box, Grid, Modal, Heading } from '@simplywallst/ui-core';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import type { DemoPortfolio } from '@/hooks/portfolio/useDemoPortfolio';
import { useTracking } from 'react-tracking';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import { DataTable } from './DataTable';
import { BarChart } from '../BarChart';
import { Tooltip } from '../Tooltip';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';

interface Props {
  selectedPeriod: Period;
  portfolio: Portfolio | DemoPortfolio;
  selectedTimestamp?: string;
  onSelectColumn?: (key: string) => void;
}

export const DetailPanel = ({
  selectedPeriod,
  portfolio,
  selectedTimestamp,
  onSelectColumn,
}: Props) => {
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  const { trackEvent } = useTracking();
  const { id: portfolioId, currencyISO, isAnalysisRestricted } = portfolio;
  const { data: portfolioDividendHoldingForecast } =
    usePortfolioHoldingsDividendForecastCreator(currencyISO)({ portfolioId });

  const dividendData = useMemo(() => {
    return extractDividendQualityByPeriod(portfolioDividendHoldingForecast)[
      selectedPeriod
    ];
  }, [portfolioDividendHoldingForecast, selectedPeriod]);

  const holdings = useMemo(() => {
    const holdings = dividendData?.holdings || [];
    const excludedHoldings = dividendData?.excludedHoldings || [];
    return [...holdings, ...excludedHoldings];
  }, [dividendData?.excludedHoldings, dividendData?.holdings]);

  const totalPreviewHoldings = 5;
  const totalHoldings = holdings.length;
  const showAllHoldingsButton = totalPreviewHoldings < totalHoldings;
  const previewHoldings = holdings.slice(0, totalPreviewHoldings);

  return (
    <>
      <DataTable
        holdingsList={previewHoldings}
        currencyISO={currencyISO}
        isAnalysisRestricted={isAnalysisRestricted}
      />
      {showAllHoldingsButton && (
        <Box marginTop="x1">
          <Button
            onClick={() => setShowAllHoldings(true)}
            styleType="gentleLarge"
            display="block"
          >
            Show all
          </Button>
        </Box>
      )}
      {showAllHoldings && (
        <Modal isNaked>
          <ModalControlVariables>
            <Box
              position="absolute"
              top="0"
              left="0"
              paddingXMd="x2"
              paddingXLg="x5"
              backgroundColor="noir"
              width="100vw"
              height="100dvh"
              overflow="hidden auto"
            >
              <Box position="sticky" top="0" zIndex="2">
                <Box
                  position="absolute"
                  top="var(--modal-cross-btn-top)"
                  right="var(--modal-cross-btn-right)"
                >
                  <Button
                    styleType="passiveLarge"
                    onClick={() => setShowAllHoldings(false)}
                    aria-label="Close"
                  >
                    <CrossSolid />
                  </Button>
                </Box>
              </Box>
              <Grid templateColumns="var(--template-columns)" color="white">
                <Box
                  position="sticky"
                  top="0"
                  backgroundColor="noir"
                  height="var(--chart-height)"
                  paddingTopMd="x3"
                >
                  <Box
                    paddingLeft="x2"
                    paddingRight="x5"
                    paddingY="x2"
                    paddingMd="x0"
                  >
                    <Heading
                      as="h3"
                      typography="xLargeStrong"
                      color="textSolid"
                      lineClamp={1}
                    >
                      Estimated Future Payments
                    </Heading>
                  </Box>
                  <Box paddingTopMd="x2" paddingLeft="x1" paddingLeftMd="x0">
                    <Tooltip selectedTimestamp={selectedTimestamp} />
                  </Box>
                  <BarChart
                    selectedColumnKey={selectedTimestamp}
                    onSelectColumn={onSelectColumn}
                    portfolio={portfolio}
                  />
                </Box>
                <Box paddingX="x1" paddingBottom="x1">
                  <DataTable
                    holdingsList={holdings}
                    currencyISO={currencyISO}
                    isAnalysisRestricted={isAnalysisRestricted}
                  />
                </Box>
              </Grid>
            </Box>
          </ModalControlVariables>
        </Modal>
      )}
      <CtaButton
        marginTop
        as="a"
        href="/discover/investing-ideas/207/upcoming-dividends"
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'discoverUpcomingDividends',
          });
        }}
        styleType="passiveLarge"
      >
        Discover Upcoming Payment Dividend Stocks
        <ChevronRightSmallSolid />
      </CtaButton>
    </>
  );
};
